import { ReactNode } from 'react'
import CloseButton from '../../elements/close-button'
import BaseOverlay from './base-overlay'
import { ReactChildren } from '../../../../types/common'

export type ModalOverlaySize = 'adaptive' | 'small' | 'large' | 'xl'

export interface ModalOverlayProps extends ReactChildren {
  title?: ReactNode
  onDismiss: () => void
  show: boolean
  size?: ModalOverlaySize
  dismissOnEscape?: boolean
  dismissOnOutsideClick?: boolean
  noHeader?: boolean
}

export default function ModalOverlay({
  children,
  title,
  onDismiss,
  show,
  size = 'small',
  dismissOnEscape = true,
  dismissOnOutsideClick = true,
  noHeader,
}: ModalOverlayProps): JSX.Element {
  const modalWidth =
    size === 'small'
      ? 'md:tw-w-modal-overlay-small'
      : size === 'large'
      ? 'md:tw-w-modal-overlay-large'
      : size === 'xl'
      ? 'md:tw-w-modal-overlay-xl'
      : size === 'adaptive'
      ? ''
      : 'md:tw-w-modal-overlay-small'

  return (
    <BaseOverlay
      className="tw-z-modal tw-fixed tw-top-16 tw-left-1/2 tw--translate-x-1/2 tw-h-auto"
      onDismiss={onDismiss}
      show={show}
      dismissOnEscape={dismissOnEscape}
      dismissOnOutsideClick={dismissOnOutsideClick}>
      <div
        className={`tw-flex ${modalWidth} tw-flex-col tw-items-center tw-justify-center tw-h-auto tw-rounded tw-bg-white tw-my-5 tw-mx-auto`}>
        <div className="tw-w-full tw-h-full tw-shadow-2xl">
          {!noHeader && (
            <div className="tw-bg-system-white tw-custom-bold-uppercase tw-w-full tw-flex tw-justify-between tw-py-4 tw-px-4 tw-rounded tw-border-b tw-border-system-grey-300 tw-items-center tw-flex-row-reverse">
              <CloseButton onClick={onDismiss} />
              {title && <h2 className="tw-m-0 tw-text-xl">{title}</h2>}
            </div>
          )}
          {children}
        </div>
      </div>
    </BaseOverlay>
  )
}

interface ModalOverlayContentProps extends ReactChildren {
  className?: string
  /**
   * When false, the content is a bounded flex column instead of a scroll
   * container, so a child can take the remaining height and scroll on its own
   * while its siblings stay in view.
   */
  scrollable?: boolean
}

/** The body of a modal, capped to the modal height and scrolling by default. */
export function ModalOverlayContent({
  children,
  className,
  scrollable = true,
}: ModalOverlayContentProps): JSX.Element {
  return (
    <div
      className={`tw-p-4 tw-w-full tw-max-h-modal ${
        scrollable ? 'tw-overflow-auto' : 'tw-flex tw-flex-col'
      } ${className}`}>
      {children}
    </div>
  )
}

export function ModalOverlayFooter({ children }: ReactChildren): JSX.Element {
  return (
    <div className="tw-py-5 tw-px-4 tw-bg-system-grey-50 tw-w-full tw-rounded">
      {children}
    </div>
  )
}
